import { buildApiBaseUrl } from './apiUrl';
import { ApiError, ApiResponseRaw } from '../types';

function isFormDataBody(body: unknown): body is FormData {
  return Object.prototype.toString.call(body) === '[object FormData]';
}

export type HttpClientErrorContext<T = unknown> = {
  tenantId: string;
  baseUrl: string;
  path: string;
  options: RequestInit;
  response: ApiResponseRaw<T>;
};

export type HttpClientOptions = {
  onError?: (ctx: HttpClientErrorContext) => void | Promise<void>;
};

type RequestInitWithErrorHandling = RequestInit & {
  skipErrorHandler?: boolean;
};

async function safeCall<TArgs extends any[]>(
  fn: ((...args: TArgs) => void | Promise<void>) | undefined,
  ...args: TArgs
) {
  if (!fn) return;
  try {
    await fn(...args);
  } catch (e) {
    // Never let the global error hook break the request flow.
    console.warn('http.onError hook threw', e);
  }
}

export function createHttpClient(tenantId: string, clientOptions: HttpClientOptions = {}) {
  const BASE_URL = buildApiBaseUrl(tenantId);

  async function parseBody(res: Response): Promise<{ json: any | null; text: string | null }> {
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      try {
        return { json: await res.json(), text: null };
      } catch {
        return { json: null, text: null };
      }
    }

    try {
      return { json: null, text: await res.text() };
    } catch {
      return { json: null, text: null };
    }
  }

  // does NOT throw on 400/500; returns status + body
  async function requestRaw<T>(
    path: string,
    options: RequestInitWithErrorHandling = {},
  ): Promise<ApiResponseRaw<T>> {
    const { skipErrorHandler, ...fetchOptions } = options;
    const isFormData = isFormDataBody(options.body);
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(options.headers ?? {}),
      },
    });

    const isError = !res.ok;

    const { json, text } = await parseBody(res);

    const apiResponse: ApiResponseRaw<T> = {
      ok: res.ok,
      status: res.status,
      data: (json ?? null) as T | null,
      text: text ?? undefined,
      errors: isError ? (json?.errors ?? json ?? text ?? undefined) : undefined,
    };

    if (isError && !skipErrorHandler) {
      await safeCall(clientOptions.onError, {
        tenantId,
        baseUrl: BASE_URL,
        path,
        options: fetchOptions,
        response: apiResponse,
      });
    }

    return {
      ok: res.ok,
      status: res.status,
      data: (json ?? null) as T | null,
      text: text ?? undefined,
      errors: isError ? (json?.errors ?? json ?? text ?? undefined) : undefined,
    };
  }

  // Existing behavior: throws on non-2xx
  async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await requestRaw<T>(path, options);

    if (!res.ok) {
      const err = new Error(`${res.status}: ${res.text ?? JSON.stringify(res.data)}`) as ApiError;
      err.status = res.status;
      err.data = res.data ?? undefined;
      err.responseText = res.text;
      throw err;
    }

    return res.data as T;
  }

  return {
    //  Existing API (throws on 400+)
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body) }),

    // New API (never throws on 400+, gives you status)
    getRaw: <T>(path: string) => requestRaw<T>(path),
    postRaw: <T>(path: string, body: unknown, options: RequestInit = {}) =>
      requestRaw<T>(path, { method: 'POST', body: JSON.stringify(body), ...options, }),

    postRawFiles: <T>(path: string, fd: FormData) =>
      requestRaw<T>(path, { method: 'POST', body: fd }),
  };
}
